import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { useCountry, COUNTRIES } from "@/lib/country-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Utensils, CheckCircle2, MapPin, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

const LOC = {
  IDLE: "idle",
  LOCATING: "locating",
  GRANTED: "granted",
  DENIED: "denied",
  ERROR: "error",
  UNAVAILABLE: "unavailable",
};

export default function SignupPage() {
  const { signupCustomer } = useAuth();
  const { setCountry, lockToCountry } = useCountry();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    country: "GH",
    phone_local: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [coords, setCoords] = useState(null);
  const [locState, setLocState] = useState(LOC.IDLE);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setPhoneLocal = (raw) =>
    setForm((f) => ({ ...f, phone_local: (raw || "").replace(/\D/g, "") }));

  const dialCode = COUNTRIES[form.country].dialCode;
  const fullPhone = `${dialCode}${form.phone_local.replace(/^0+/, "")}`;

  // Silent, single geolocation request — same pattern as sign-in, just gated
  // (signup needs real coordinates to show nearby restaurants; sign-in only
  // uses location as a nicety default for the country dropdown).
  const requestLocation = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setLocState(LOC.UNAVAILABLE);
      return;
    }
    setLocState(LOC.LOCATING);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const next = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
        setCoords(next);
        setLocState(LOC.GRANTED);
        // Best-effort country auto-detect from the same coordinates — silent.
        api
          .get("/geocode/reverse", { params: { lat: next.latitude, lon: next.longitude } })
          .then(({ data }) => {
            if (data.country_code === "GH" || data.country_code === "NG") {
              setForm((f) => ({ ...f, country: data.country_code }));
            }
          })
          .catch(() => {
            // silent — dropdown just keeps its current default
          });
      },
      (err) => {
        setLocState(err && err.code === err.PERMISSION_DENIED ? LOC.DENIED : LOC.ERROR);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  }, []);

  useEffect(() => {
    requestLocation();
  }, [requestLocation]);

  // Sync country -> currency
  useEffect(() => {
    if (form.country === "GH" || form.country === "NG") setCountry(form.country);
  }, [form.country, setCountry]);

  const locationReady = locState === LOC.GRANTED && coords;

  const submit = async (e) => {
    e.preventDefault();
    if (!locationReady) {
      toast({ title: "Enable location to continue", variant: "destructive" });
      return;
    }
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast({ title: "Enter your first and last name", variant: "destructive" });
      return;
    }
    if (form.password.length < 6) {
      toast({ title: "Password must be at least 6 characters", variant: "destructive" });
      return;
    }
    if (form.phone_local.length < 6) {
      toast({ title: "Enter a valid phone number", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await signupCustomer({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: fullPhone,
        country: form.country,
        password: form.password,
        latitude: coords.latitude,
        longitude: coords.longitude,
      });
      lockToCountry(form.country);   // permanently lock the country selector
      toast({ title: "Welcome to Cravve!" });
      navigate("/", { replace: true });
    } catch (err) {
      toast({
        title: "Sign up failed",
        description: err?.response?.data?.detail || "Try again",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6" data-testid="signup-logo">
            <div className="w-12 h-12 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center">
              <Utensils className="w-6 h-6" />
            </div>
          </Link>
          <h1 className="font-serif text-3xl font-bold">Join Cravve</h1>
          <p className="text-muted-foreground mt-2">Discover West African food, ordered conversationally.</p>
        </div>

        <form onSubmit={submit} className="bg-card border rounded-2xl p-6 space-y-4 shadow-sm">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="first-name">First name</Label>
              <Input id="first-name" required value={form.first_name} onChange={upd("first_name")}
                placeholder="Adwoa" data-testid="signup-first-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last-name">Last name</Label>
              <Input id="last-name" required value={form.last_name} onChange={upd("last_name")}
                placeholder="Mensah" data-testid="signup-last-name-input" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone number</Label>
            <div className="flex items-stretch h-10 rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 overflow-hidden">
              <div
                className="flex items-center gap-1 px-3 bg-muted text-muted-foreground text-sm font-semibold select-none border-r"
                data-testid="signup-dial-code"
                title="Detected from your location"
              >
                <span className="text-base leading-none">{COUNTRIES[form.country].flag}</span>
                <span>{dialCode}</span>
              </div>
              <input
                id="phone"
                type="tel"
                inputMode="numeric"
                required
                value={form.phone_local}
                onChange={(e) => setPhoneLocal(e.target.value)}
                placeholder={form.country === "GH" ? "24 123 4567" : "801 234 5678"}
                className="flex-1 bg-transparent px-3 text-sm placeholder:text-muted-foreground focus:outline-none"
                data-testid="signup-phone-input"
                autoComplete="tel-national"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Country code is detected from your location — just enter your local number.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" required minLength={6} value={form.password}
              onChange={upd("password")} placeholder="At least 6 characters" data-testid="signup-password-input" />
          </div>

          <div className="space-y-2">
            <Label>Location</Label>
            {locState === LOC.LOCATING && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 rounded-xl border bg-muted/30"
                data-testid="signup-location-locating">
                <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                Detecting your location…
              </div>
            )}
            {locationReady && (
              <div className="flex items-center gap-2 text-sm text-primary p-3 rounded-xl border border-primary/20 bg-primary/5"
                data-testid="signup-location-granted">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                Location detected
              </div>
            )}
            {(locState === LOC.DENIED || locState === LOC.ERROR || locState === LOC.UNAVAILABLE) && (
              <div className="space-y-2 p-3 rounded-xl border border-destructive/30 bg-destructive/5"
                data-testid="signup-location-error">
                <div className="flex items-start gap-2 text-sm text-destructive">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    {locState === LOC.DENIED &&
                      "Location access is blocked. Please allow it in your browser settings, then try again."}
                    {locState === LOC.ERROR &&
                      "Couldn't detect your location. Please try again."}
                    {locState === LOC.UNAVAILABLE &&
                      "Your browser doesn't support location services — please use a modern browser to sign up."}
                  </span>
                </div>
                {locState !== LOC.UNAVAILABLE && (
                  <Button type="button" variant="outline" size="sm" onClick={requestLocation}
                    className="gap-1.5" data-testid="signup-location-retry">
                    <MapPin className="w-3.5 h-3.5" />
                    Try again
                  </Button>
                )}
              </div>
            )}
          </div>

          <Button
            type="submit"
            className="w-full h-11 font-semibold"
            disabled={loading || !locationReady}
            data-testid="signup-submit-btn"
          >
            {loading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : !locationReady
                ? "Enable location to continue"
                : "Create account"}
          </Button>
        </form>

        <p className="text-center mt-6 text-sm text-muted-foreground">
          Already on Cravve?{" "}
          <Link to="/signin" className="text-primary font-semibold hover:underline" data-testid="signup-signin-link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
